import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';

interface DropDownActionButtonProps {
  label?: string;
  dropdownOptions?: Array<{
    id: string | number;
    label: string;
    value: string;
    isDisabled?: boolean;
    iconName?: string;
  }>;
  onDropdownSelect?: (option: {
    id: string | number;
    label: string;
    value: string;
  }) => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  leftIconName?: string;
}

const DropDownActionButton: React.FC<DropDownActionButtonProps> = ({
  label,
  dropdownOptions = [],
  onDropdownSelect,
  className = '',
  disabled = false,
  loading = false,
  leftIconName
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = () => {
    if (!disabled && !loading) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionClick = (option: {
    id: string | number;
    label: string;
    value: string;
  }) => {
    if (onDropdownSelect) {
      onDropdownSelect(option);
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className={clsx('relative inline-flex', className)} ref={dropdownRef}>
      <Button
        handleClick={handleDropdownToggle}
        variant="primary"
        size="none"
        label={label}
        leftIconName={leftIconName}
        leftIconStyle="!size-3"
        className="py-3 pl-4 pr-2"
        disabled={disabled}
        loading={loading}
        rightIconName="down-icon-white"
        rightIconStyle={isDropdownOpen ? 'ml-3 rotate-180' : 'ml-3'}
      />

      {isDropdownOpen && dropdownOptions.length > 0 && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-70 rounded-2xl bg-white shadow-lg">
          <div className="flex flex-col gap-2 p-3">
            {dropdownOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => handleOptionClick(option)}
                disabled={option.isDisabled}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left transition-colors hover:bg-grey10',
                  option.isDisabled
                    ? 'cursor-not-allowed text-grey40'
                    : 'text-content'
                )}
              >
                {option.iconName && (
                  <div className="flex size-5 items-center justify-center">
                    <Icon name={option.iconName} className="size-5" />
                  </div>
                )}
                <div className="text-sm font-medium">{option.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropDownActionButton;
